import { useEffect, useRef, useMemo } from 'react';
import {
	LuPin,
	LuPencil,
	LuTrash2,
	LuX,
	LuCalendar,
	LuUser,
	LuEye,
	LuRefreshCw,
} from 'react-icons/lu';
import { Popconfirm, Tag } from 'antd';
import { motion } from 'motion/react';
import { ml } from 'src/_shared/services/utils.js';
import { sanitizeWithIframes } from './utils.js';
import { formatDate, darkenHex } from './utils.js';
import { getAntTagColor } from './constants.js';

function AnnouncementDetail({
	announcement,
	isModerator,
	onClose,
	onEdit,
	onDelete,
	languageCode,
	currentUser,
	allMultiLingualData,
	preloadedThumbnailUrl,
	showAuthor,
	showViews,
	defaultAuthor,
	pinnedColor,
}) {
	const sanitizedContent = useMemo(
		() => sanitizeWithIframes(announcement.content || ''),
		[announcement.content]
	);

	const thumbnailUrl =
		preloadedThumbnailUrl || announcement.thumbnailSignedUrl || null;
	const outerScrollRef = useRef(null);
	const contentRef = useRef(null);
	const articleRef = useRef(null);
	const titleRef = useRef(null);
	const imageRef = useRef(null);
	const overlayRef = useRef(null);

	const updateImageEffects = (scrollTop) => {
		const img = imageRef.current;
		const overlay = overlayRef.current;
		if (!img) return;
		const progress = Math.min(1, scrollTop / 192); // 0 to 1
		const scale = 1 + progress * 0.12;
		const blur = progress * 3;
		const translateY = scrollTop * 0.25;
		img.style.transform = `scale(${scale}) translateY(${translateY}px)`;
		img.style.filter = `blur(${blur}px)`;
		if (overlay) {
			overlay.style.opacity = progress * 0.3;
		}
	};

	useEffect(() => {
		const outer = outerScrollRef.current;
		if (!outer || !thumbnailUrl) return;

		// Single virtual scroll position: 0..192 = outer, 192+ = article
		let virtualPos = 0;

		const getOverflow = () => {
			const article = articleRef.current;
			const titleEl = titleRef.current;
			if (!article) return 0;
			// Total content = title height + article's full content height + image
			const titleHeight = titleEl ? titleEl.offsetHeight : 0;
			const articleFullHeight =
				article.scrollHeight + (article.offsetHeight - article.clientHeight); // include padding
			return Math.max(
				0,
				titleHeight + articleFullHeight + 256 - outer.clientHeight
			);
		};

		const applyScroll = () => {
			const article = articleRef.current;
			if (!article) return;

			const title = titleRef.current;
			if (virtualPos <= 192) {
				outer.scrollTop = virtualPos;
				article.scrollTop = 0;
				updateImageEffects(virtualPos);
				if (title) title.style.boxShadow = 'none';
			} else {
				outer.scrollTop = 192;
				const articleOverflow = Math.max(0, getOverflow() - 192);
				article.scrollTop = Math.min(articleOverflow, virtualPos - 192);
				updateImageEffects(192);
				if (title) title.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
			}
		};

		const handleWheel = (e) => {
			const article = articleRef.current;
			if (!article) return;
			e.preventDefault();

			const overflow = getOverflow();
			const maxVirtual = overflow;

			virtualPos = Math.max(0, Math.min(maxVirtual, virtualPos + e.deltaY));
			applyScroll();
		};

		// Touch handling with inertia
		let touchStartY = 0;
		let lastTouchY = 0;
		let lastTouchTime = 0;
		let velocity = 0;
		let inertiaFrame = null;

		const stopInertia = () => {
			if (inertiaFrame) {
				cancelAnimationFrame(inertiaFrame);
				inertiaFrame = null;
			}
		};

		const startInertia = () => {
			const friction = 0.97;
			const minVelocity = 0.5;

			const tick = () => {
				velocity *= friction;
				if (Math.abs(velocity) < minVelocity) {
					inertiaFrame = null;
					return;
				}

				const overflow = getOverflow();
				virtualPos = Math.max(0, Math.min(overflow, virtualPos + velocity));
				applyScroll();

				inertiaFrame = requestAnimationFrame(tick);
			};

			inertiaFrame = requestAnimationFrame(tick);
		};

		const handleTouchStart = (e) => {
			stopInertia();
			touchStartY = e.touches[0].clientY;
			lastTouchY = touchStartY;
			lastTouchTime = Date.now();
			velocity = 0;
		};

		const handleTouchMove = (e) => {
			const article = articleRef.current;
			if (!article) return;
			e.preventDefault();

			const touchY = e.touches[0].clientY;
			const now = Date.now();
			const delta = lastTouchY - touchY;
			const dt = now - lastTouchTime;

			if (dt > 0) {
				velocity = (delta / dt) * 32;
			}

			lastTouchY = touchY;
			lastTouchTime = now;

			const overflow = getOverflow();
			virtualPos = Math.max(0, Math.min(overflow, virtualPos + delta));
			applyScroll();
		};

		const handleTouchEnd = () => {
			if (Math.abs(velocity) > 1) {
				startInertia();
			}
		};

		outer.addEventListener('wheel', handleWheel, { passive: false });
		outer.addEventListener('touchstart', handleTouchStart, { passive: true });
		outer.addEventListener('touchmove', handleTouchMove, { passive: false });
		outer.addEventListener('touchend', handleTouchEnd, { passive: true });
		return () => {
			stopInertia();
			outer.removeEventListener('wheel', handleWheel);
			outer.removeEventListener('touchstart', handleTouchStart);
			outer.removeEventListener('touchmove', handleTouchMove);
			outer.removeEventListener('touchend', handleTouchEnd);
		};
	}, [thumbnailUrl]);

	useEffect(() => {
		const handleEsc = (e) => {
			if (e.key === 'Escape') onClose();
		};

		document.addEventListener('keydown', handleEsc);
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', handleEsc);
			document.body.style.overflow = '';
		};
	}, [onClose]);

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center p-4"
			onClick={onClose}
		>
			{/* Backdrop */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				exit={{ opacity: 0 }}
				transition={{ duration: 0.3 }}
				className="fixed inset-0"
				style={{
					background: 'rgba(15,23,42,0.5)',
					backdropFilter: 'blur(4px)',
				}}
			/>

			{/* Modal */}
			<motion.div
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				exit={{ opacity: 0, y: 20 }}
				transition={{ duration: 0.25, ease: 'easeOut' }}
				className="relative w-full overflow-hidden rounded-2xl bg-white"
				style={{
					maxWidth: 672,
					maxHeight: '85vh',
					boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
					display: 'flex',
					flexDirection: 'column',
				}}
				onClick={(e) => e.stopPropagation()}
			>
				{/* Close button */}
				<button
					type="button"
					onClick={onClose}
					style={{
						position: 'sticky',
						top: 16,
						alignSelf: 'flex-end',
						marginRight: 16,
						zIndex: 20,
						width: 32,
						height: 32,
						borderRadius: 12,
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						padding: 0,
						lineHeight: 0,
						background: 'rgba(255,255,255,0.9)',
						backdropFilter: 'blur(8px)',
						color: '#475569',
						border: 'none',
						cursor: 'pointer',
						boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
						transition: 'all 0.2s',
						flexShrink: 0,
						marginBottom: -48,
					}}
					onMouseOver={(e) => {
						e.currentTarget.style.color = '#475569';
						e.currentTarget.style.background = '#f1f5f9';
					}}
					onMouseOut={(e) => {
						e.currentTarget.style.color = '#475569';
						e.currentTarget.style.background = 'rgba(255,255,255,0.9)';
					}}
				>
					<LuX size={14} />
				</button>

				{/* Scrollable outer container for parallax effect */}
				{thumbnailUrl ? (
					<div
						ref={outerScrollRef}
						style={{
							flex: 1,
							overflowY: 'auto',
							overflowX: 'hidden',
							scrollbarWidth: 'none',
						}}
					>
						{/* Hero image — sticky at top, stays behind content */}
						<div
							style={{
								position: 'sticky',
								top: 0,
								zIndex: 0,
								height: 256,
								overflow: 'hidden',
								flexShrink: 0,
							}}
						>
							<img
								ref={imageRef}
								src={thumbnailUrl}
								alt={announcement.title}
								className="w-full object-cover"
								style={{
									height: 320,
									marginTop: -32,
									transformOrigin: 'center center',
									willChange: 'transform, filter',
								}}
							/>
							{/* Darken overlay */}
							<div
								ref={overlayRef}
								style={{
									position: 'absolute',
									inset: 0,
									background: '#000',
									opacity: 0,
									pointerEvents: 'none',
									willChange: 'opacity',
								}}
							/>
						</div>

						{/* Content wrapper — slides up over image, locks at 64px from top */}
						<div
							ref={contentRef}
							style={{
								position: 'sticky',
								top: 64,
								zIndex: 1,
								background: '#fff',
								maxHeight: 'calc(85vh - 64px)',
								display: 'flex',
								flexDirection: 'column',
								overflow: 'hidden',
							}}
						>
							{/* Title section — always visible, never scrolls */}
							<div
								ref={titleRef}
								style={{
									padding: '24px 32px 0',
									flexShrink: 0,
									position: 'relative',
									zIndex: 1,
									transition: 'box-shadow 0.2s',
								}}
							>
								{/* Tags */}
								{announcement.tags?.length > 0 && (
									<div className="mb-4 flex flex-wrap gap-1.5">
										{announcement.tags.map((tag) => (
											<Tag
												key={tag}
												color={getAntTagColor(tag)}
												style={{ margin: 0 }}
											>
												{tag}
											</Tag>
										))}
									</div>
								)}

								{/* Title */}
								<h2
									className="mb-3 leading-tight"
									style={{
										color: '#1e293b',
										fontSize: 24,
										fontWeight: 700,
										margin: 0,
										marginBottom: 12,
									}}
								>
									{announcement.title}
								</h2>

								{/* Meta */}
								<div
									className="flex items-center gap-4 pb-4"
									style={{
										color: '#475569',
										borderBottom: '1px solid #f1f5f9',
										fontSize: 14,
										lineHeight: 1,
										marginBottom: 0,
									}}
								>
									<span className="flex items-center gap-1.5">
										<LuCalendar size={13} />
										{formatDate(announcement.dateCreated, languageCode)}
									</span>
									{showAuthor && (announcement.author || defaultAuthor) && (
										<>
											<span style={{ color: '#475569', fontWeight: 700 }}>
												&middot;
											</span>
											<span className="flex items-center gap-1.5">
												<LuUser size={13} />
												{announcement.author || defaultAuthor}
											</span>
										</>
									)}
									{showViews && announcement.viewCount > 0 && (
										<>
											<span style={{ color: '#475569', fontWeight: 700 }}>
												&middot;
											</span>
											<span className="flex items-center gap-1.5">
												<LuEye size={13} />
												{announcement.viewCount}{' '}
												{ml('views', currentUser, allMultiLingualData)}
											</span>
										</>
									)}
									{announcement.pinned && (
										<span
											className="flex items-center gap-1.5"
											style={{ color: darkenHex(pinnedColor) }}
										>
											<LuPin size={13} />
											Pinned
										</span>
									)}
									{announcement.updatedAt && (
										<span className="flex items-center gap-1.5">
											<LuRefreshCw size={12} />
											{formatDate(announcement.updatedAt, languageCode)}
										</span>
									)}
								</div>
							</div>

							{/* Article body — scrollable */}
							<div
								ref={articleRef}
								style={{
									flex: 1,
									overflowY: 'auto',
									padding: '16px 32px 24px',
									scrollbarWidth: 'thin',
									scrollbarColor: '#cbd5e1 transparent',
								}}
							>
								{/* HTML Content */}
								<div
									className="prose text-sm"
									style={{ color: '#475569' }}
									dangerouslySetInnerHTML={{
										__html: sanitizedContent,
									}}
								/>

								{/* Moderator actions */}
								{isModerator && (
									<div
										className="mt-8 flex gap-2 pt-6"
										style={{ borderTop: '1px solid #f1f5f9' }}
									>
										<button
											type="button"
											onClick={() => onEdit(announcement)}
											className="flex items-center gap-2"
											style={{
												paddingLeft: 16,
												paddingRight: 16,
												paddingTop: 8,
												paddingBottom: 8,
												borderRadius: '0.75rem',
												fontSize: 14,
												fontWeight: 600,
												background: '#f1f5f9',
												color: '#475569',
												border: 'none',
												cursor: 'pointer',
												transition: 'background 0.2s',
											}}
											onMouseOver={(e) => {
												e.currentTarget.style.background = '#e2e8f0';
											}}
											onMouseOut={(e) => {
												e.currentTarget.style.background = '#f1f5f9';
											}}
										>
											<LuPencil size={14} />
											{ml('Edit', currentUser, allMultiLingualData)}
										</button>
										<Popconfirm
											title="Delete this announcement?"
											description="This action cannot be undone."
											okText="Delete"
											okType="danger"
											cancelText="Cancel"
											onConfirm={() => {
												onDelete(announcement);
												onClose();
											}}
										>
											<button
												type="button"
												className="flex items-center gap-2"
												style={{
													paddingLeft: 16,
													paddingRight: 16,
													paddingTop: 8,
													paddingBottom: 8,
													borderRadius: '0.75rem',
													fontSize: 14,
													fontWeight: 600,
													background: '#fef2f2',
													color: '#dc2626',
													border: 'none',
													cursor: 'pointer',
													transition: 'background 0.2s',
												}}
												onMouseOver={(e) => {
													e.currentTarget.style.background = '#fee2e2';
												}}
												onMouseOut={(e) => {
													e.currentTarget.style.background = '#fef2f2';
												}}
											>
												<LuTrash2 size={14} />
												{ml('Delete', currentUser, allMultiLingualData)}
											</button>
										</Popconfirm>
									</div>
								)}
							</div>
						</div>
					</div>
				) : (
					/* No thumbnail — simple scrollable content */
					<div
						className="overflow-y-auto"
						style={{
							padding: '40px 32px 24px',
							scrollbarWidth: 'thin',
							scrollbarColor: '#cbd5e1 transparent',
						}}
					>
						{/* Tags */}
						{announcement.tags?.length > 0 && (
							<div className="mb-4 flex flex-wrap gap-1.5">
								{announcement.tags.map((tag) => (
									<Tag
										key={tag}
										color={getAntTagColor(tag)}
										style={{ margin: 0 }}
									>
										{tag}
									</Tag>
								))}
							</div>
						)}

						{/* Title */}
						<h2
							className="mb-3 leading-tight"
							style={{
								color: '#1e293b',
								fontSize: 24,
								fontWeight: 700,
								margin: 0,
								marginBottom: 12,
							}}
						>
							{announcement.title}
						</h2>

						{/* Meta */}
						<div
							className="mb-6 flex items-center gap-4 pb-6"
							style={{
								color: '#475569',
								borderBottom: '1px solid #f1f5f9',
								fontSize: 14,
								lineHeight: 1,
							}}
						>
							<span className="flex items-center gap-1.5">
								<LuCalendar size={13} />
								{formatDate(announcement.dateCreated, languageCode)}
							</span>
							{showAuthor && (announcement.author || defaultAuthor) && (
								<>
									<span style={{ color: '#475569', fontWeight: 700 }}>
										&middot;
									</span>
									<span className="flex items-center gap-1.5">
										<LuUser size={13} />
										{announcement.author || defaultAuthor}
									</span>
								</>
							)}
							{showViews && announcement.viewCount > 0 && (
								<>
									<span style={{ color: '#475569', fontWeight: 700 }}>
										&middot;
									</span>
									<span className="flex items-center gap-1.5">
										<LuEye size={13} />
										{announcement.viewCount}{' '}
										{ml('views', currentUser, allMultiLingualData)}
									</span>
								</>
							)}
							{announcement.pinned && (
								<span
									className="flex items-center gap-1.5"
									style={{ color: darkenHex(pinnedColor) }}
								>
									<LuPin size={13} />
									Pinned
								</span>
							)}
							{announcement.updatedAt && (
								<span className="flex items-center gap-1.5">
									<LuRefreshCw size={12} />
									{formatDate(announcement.updatedAt, languageCode)}
								</span>
							)}
						</div>

						{/* HTML Content */}
						<div
							className="prose text-sm"
							style={{ color: '#475569' }}
							dangerouslySetInnerHTML={{
								__html: sanitizedContent,
							}}
						/>

						{/* Moderator actions */}
						{isModerator && (
							<div
								className="mt-8 flex gap-2 pt-6"
								style={{ borderTop: '1px solid #f1f5f9' }}
							>
								<button
									type="button"
									onClick={() => onEdit(announcement)}
									className="flex items-center gap-2"
									style={{
										paddingLeft: 16,
										paddingRight: 16,
										paddingTop: 8,
										paddingBottom: 8,
										borderRadius: '0.75rem',
										fontSize: 14,
										fontWeight: 600,
										background: '#f1f5f9',
										color: '#475569',
										border: 'none',
										cursor: 'pointer',
										transition: 'background 0.2s',
									}}
									onMouseOver={(e) => {
										e.currentTarget.style.background = '#e2e8f0';
									}}
									onMouseOut={(e) => {
										e.currentTarget.style.background = '#f1f5f9';
									}}
								>
									<LuPencil size={14} />
									{ml('Edit', currentUser, allMultiLingualData)}
								</button>
								<Popconfirm
									title="Delete this announcement?"
									description="This action cannot be undone."
									okText="Delete"
									okType="danger"
									cancelText="Cancel"
									onConfirm={() => {
										onDelete(announcement);
										onClose();
									}}
								>
									<button
										type="button"
										className="flex items-center gap-2"
										style={{
											paddingLeft: 16,
											paddingRight: 16,
											paddingTop: 8,
											paddingBottom: 8,
											borderRadius: '0.75rem',
											fontSize: 14,
											fontWeight: 600,
											background: '#fef2f2',
											color: '#dc2626',
											border: 'none',
											cursor: 'pointer',
											transition: 'background 0.2s',
										}}
										onMouseOver={(e) => {
											e.currentTarget.style.background = '#fee2e2';
										}}
										onMouseOut={(e) => {
											e.currentTarget.style.background = '#fef2f2';
										}}
									>
										<LuTrash2 size={14} />
										{ml('Delete', currentUser, allMultiLingualData)}
									</button>
								</Popconfirm>
							</div>
						)}
					</div>
				)}
			</motion.div>
		</div>
	);
}

export default AnnouncementDetail;
